import { FileCategories } from 'Enum';
import { DeleteModal } from 'Components';
import { forwardRef, useRef, useState } from 'react';
import { deleteFile, getFile, postFile } from 'API';
import { useAppSelector, useDispatchApp } from 'Hooks';
import { addFile, removeFile } from 'Store';
import { Loader } from 'Components/Loader';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { FileCard } from './FileCard';
import { UploadFileButton } from './UploadFileButton';
import { FileRow } from './FileRow';
import { Divider } from '@mui/material';
import { FileError } from './UploadFileButton/FileError';
import { FILE_ERROR_TYPES } from 'Enum/FileErrorTypes';
import { validateFile } from 'Utils/fileValidation';

export interface IFileUpload {
  fileCategoryId: FileCategories;
  caseDetailsId?: number;
  witnessId?: number;
  hospitalizationId?: number;
  fileLabel: string;
  loading?: boolean;
  ariaLabel?: string;
  dataRequired?: boolean;
  disabled?: boolean;
}

export const FileUpload = forwardRef<HTMLButtonElement, IFileUpload>(
  (props, ref) => {
    const dispatch = useDispatchApp();
    const dictionary = Dictionary[DICTIONARY.GENERAL];

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [showError, setShowError] = useState<boolean>(false);
    const [errorType, setErrorType] = useState<number | null>(null);

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    const [currFileId, setCurrFileId] = useState<number | null>(null);
    const [currFileName, setCurrFileName] = useState<string>('');

    const fileIds = useAppSelector(({ file: { files } }) => {
      const categoryFiles = files[props.fileCategoryId];
      if (props.caseDetailsId && props.hospitalizationId) {
        return categoryFiles?.filter(
          (file) =>
            file.caseDetailsRelationId === props.caseDetailsId &&
            file.hospitalizationDetailsRelationId === props.hospitalizationId
        );
      } else if (props.caseDetailsId && props.witnessId) {
        return categoryFiles?.filter(
          (file) =>
            file.caseDetailsRelationId === props.caseDetailsId &&
            file.witnessId === props.witnessId
        );
      } else if (props.caseDetailsId) {
        return categoryFiles?.filter(
          (file) => file.caseDetailsRelationId === props.caseDetailsId
        );
      } else if (props.hospitalizationId) {
        return categoryFiles?.filter(
          (file) =>
            file.hospitalizationDetailsRelationId === props.hospitalizationId
        );
      } else {
        return files[props.fileCategoryId];
      }
    });

    const onChangeHandler = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setErrorType(null);
      const file = event.target.files?.[0];
      if (!file) return;

      const isValid = await validateFile(file);

      if (!isValid) {
        setShowError(true);
        setErrorType(FILE_ERROR_TYPES.FILE_TYPE_INVALID);
        return;
      }

      setIsLoading(true);

      const cleanName = file.name.trim();
      const nameWithoutExtension = cleanName.split('.').slice(0, -1).join('.');
      const sanitizedBaseName = nameWithoutExtension.replace(
        /[^a-zA-Z0-9\u05D0-\u05EA\- _]/g,
        ''
      );
      const fileExt = cleanName.split('.').pop();
      const fileName = `${sanitizedBaseName}-${Date.now()}.${fileExt}`;

      await postFileHandler(file, fileName);
      setErrorType(null);
      setShowError(false);
    };

    const fileInput = useRef<HTMLInputElement | null>(null);

    const postFileHandler = async (file: File, fileName: string) => {
      const form = new FormData();
      form.append('file', file, fileName);
      const fileData = await postFile(
        props.fileCategoryId,
        form,
        props.caseDetailsId,
        props.witnessId,
        props.hospitalizationId
      );
      setIsLoading(false);
      if (fileData) {
        setShowError(false);
        setErrorType(null);
        dispatch(
          addFile({ file: fileData, fileCategory: props.fileCategoryId })
        );
      } else {
        setShowError(true);
        setErrorType(FILE_ERROR_TYPES.FILE_TYPE_INVALID);
      }
    };

    const deleteFileHandler = async (fileId: number, fileName: string) => {
      setCurrFileId(fileId);
      setCurrFileName(fileName);
      setShowDeleteModal(true);
    };

    const deleteModal = async (fileId: number) => {
      const fileData = await deleteFile(fileId);
      if (fileData) {
        dispatch(
          removeFile({
            fileCategory: props.fileCategoryId,
            fileId,
          })
        );
        if (fileInput.current) {
          fileInput.current.value = '';
        }
      }

      setShowDeleteModal(false);
    };

    const closeDeleteModalHandler = () => {
      setCurrFileId(null);
      setCurrFileName('');
      setShowDeleteModal(false);
    };

    const watchFile = async (fileId: number, userGivenName: string) => {
      if (fileId) {
        const fileData = await getFile(fileId, userGivenName);
        if (fileData) window.open(URL.createObjectURL(fileData), userGivenName);
      }
    };

    const clickFileInput = () => {
      if (fileInput.current) {
        fileInput.current.click();
      }
    };

    return (
      <>
        <input
          ref={(e) => {
            fileInput.current = e;
          }}
          type='file'
          accept='.doc,.docx,.jpg,.png,.pdf,.tif,tiff,jpeg'
          hidden
          onChange={onChangeHandler}
        />
        {showDeleteModal && currFileId && (
          <DeleteModal
            isVisible={showDeleteModal}
            closeModal={closeDeleteModalHandler}
            deleteModal={() => deleteModal(currFileId)}
            fileName={currFileName}
          />
        )}
        {fileIds && fileIds.length ? (
          <>
            <FileCard clickHandler={clickFileInput} fileLabel={props.fileLabel}>
              <Divider />
              {fileIds.map((file, index) => (
                <>
                  <FileRow
                    key={file.id}
                    fileName={file.userGivenName}
                    watchFile={() => watchFile(file.id, file.userGivenName)}
                    deleteFile={() =>
                      deleteFileHandler(file.id, file.userGivenName)
                    }
                  />
                  {index !== fileIds.length - 1 ? <Divider /> : null}
                </>
              ))}
            </FileCard>
          </>
        ) : (
          <UploadFileButton
            fileCategoryId={props.fileCategoryId}
            caseDetailsId={props.caseDetailsId}
            hospitalizationId={props.hospitalizationId}
            dataRequired={props.dataRequired}
            clickHandler={clickFileInput}
            ref={ref}
            ariaLabel={props?.ariaLabel}
            disabled={props.disabled}
            error={showError}
          >
            {showError
              ? dictionary.fileUploadTryAgain
              : dictionary.fileUploadLabel}
          </UploadFileButton>
        )}
        {showError && errorType ? <FileError errorType={errorType} /> : null}
        {isLoading && <Loader />}
      </>
    );
  }
);
